import { useLayoutEffect, useRef, useState } from 'react';

import {
  getEfficiencyTrend,
  getServiceRiskHeatmap,
  getWhatIfComparison,
} from '../../../api/analyticsApi';
import type { Run } from '../../../types';
import {
  buildComparatorData,
  calculateEfficiencyMultiplier,
  hasUsableWhatIfComparison,
  mapHeatmapCell,
  mapTrendPoint,
  type AnalyticsData,
  type ComparatorBar,
  type HeatmapCell,
  type TrendPoint,
} from '../utils/analyticsTransforms';

/**
 * Load analytics data for the current run and optional project.
 *
 * @param currentRun - Current run used as the analytics reference.
 * @param projectId - Optional project identifier.
 * @returns Loaded analytics data with loading and error state.
 */
export function useRunAnalytics(currentRun: Run, projectId?: string) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const requestIdRef = useRef(0);

  useLayoutEffect(() => {
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    const { signal } = controller;

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        setData(null);

        const runType = (currentRun as any).run_type || '';
        const runData = currentRun as any;
        const currentRunId = runData.run_id ?? runData.id;

        const [efficiencyRes, heatmapRes] = await Promise.all([
          getEfficiencyTrend(projectId, { days: 30, runType }, signal),
          getServiceRiskHeatmap(
            projectId,
            { numRuns: 5, topServices: 8, runType, currentRunId },
            signal
          ),
        ]);

        if (!efficiencyRes.ok || !heatmapRes.ok) {
          throw new Error('Failed to fetch analytics data');
        }

        const efficiencyData = await efficiencyRes.json();
        const filteredTrend = efficiencyData.data.filter(
          (point: any) =>
            (point.status === 'completed' || point.status == null) &&
            point.risk_coverage > 0 &&
            point.tests_executed_pct > 0
        );

        const heatmapData = await heatmapRes.json();

        const trendData: TrendPoint[] = filteredTrend.map(mapTrendPoint);

        const heatmapCells: HeatmapCell[] = heatmapData.data.map(mapHeatmapCell);

        const effectiveProjectId = projectId || runData.project_id;
        let comparatorData: ComparatorBar[] = [];
        let comparatorError: string | undefined;
        let efficiencyMultiplier = 1.0;

        try {
          const comparisonRes = await getWhatIfComparison(
            effectiveProjectId,
            { days: 30, runId: currentRunId },
            signal
          );

          if (!comparisonRes.ok) {
            throw new Error(`What-if comparison API failed: ${comparisonRes.status}`);
          }

          const comparisonPayload = await comparisonRes.json();

          if (hasUsableWhatIfComparison(comparisonPayload)) {
            comparatorData = buildComparatorData(comparisonPayload);
            efficiencyMultiplier =
              comparisonPayload.efficiency_multiplier ||
              calculateEfficiencyMultiplier(
                comparatorData[0]?.coveragePct ?? 0,
                comparatorData
              );
          } else {
            comparatorError =
              'What-if comparison is unavailable until measured project baseline data exists.';
          }
        } catch (comparisonErr) {
          if ((comparisonErr as any)?.name === 'AbortError') return;
          console.error('Failed to fetch what-if comparison:', comparisonErr);
          comparatorError =
            'What-if comparison could not be loaded. No estimated comparison values are shown.';
        }

        if (requestId !== requestIdRef.current) return;

        setData({
          trendData,
          heatmapRuns: heatmapData.runs,
          heatmapData: heatmapCells,
          comparatorData,
          efficiencyMultiplier,
          comparatorError,
        });

        setError(null);
      } catch (err) {
        if ((err as any)?.name === 'AbortError' || requestId !== requestIdRef.current) return;
        console.error('Failed to fetch analytics:', err);
        setError('Failed to load analytics data. Please try again.');
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }

    void fetchData();

    return () => controller.abort();
  }, [currentRun, projectId]);

  return { data, loading, error };
}
